import { createSlice } from "@reduxjs/toolkit";
import { pageSize } from "../../helpers";

const bidTableData = {
  onPage: 0,
  pageSize: 0,
  drmMaintenanceDetails: [],
  drmUpdatedMaintenanceDetails: {},
  originalData: {}, 
  modifiedRows: {}, 
  selectedRows: [], 
  totalPages: 0,
  totalRecords: 0,
  isLoading: false,
  errorMessage: undefined,
};

const dailyTableData = {
  onPage: 0,
  pageSize: 0,
  drmMaintenanceDetails: [],
  drmUpdatedMaintenanceDetails: {},
  originalData: {}, 
  modifiedRows: {}, 
  selectedRows: [], 
  newAddedRows: [], 
  totalPages: 0,
  totalRecords: 0,
  isLoading: false,
  errorMessage: undefined,
};

const drmTableDataSlice = createSlice({
  name: "drmTableData",
  initialState: {
    drmBidTableData: bidTableData,
    drmDailyTableData: dailyTableData,
    isLoading: false,
    errorMessage: undefined,
  },
  reducers: {
    getDrmTableData: (state) => {
      state.isLoading = true;
    },
    setDrmTableData: (state, action) => {
      const { records: newRecords, tableType } = action.payload;
      const newPageSize = pageSize;
      
      const targetTable = tableType === "drmBidTableData" ? state.drmBidTableData : state.drmDailyTableData;
      const totalRecords = targetTable.totalRecords + newRecords.length;
      
      const updatedDetails = [...newRecords, ...targetTable.drmMaintenanceDetails];

      const newAddedIndices = newRecords.map((_, index) => index);
      
      targetTable.drmMaintenanceDetails = updatedDetails;
      targetTable.totalRecords = totalRecords;
      targetTable.pageSize = newPageSize;
      targetTable.totalPages = Math.ceil(totalRecords / newPageSize);
      targetTable.isLoading = false;
      
      if (tableType === "drmDailyTableData") {
        targetTable.newAddedRows = [
          ...newAddedIndices,
          ...targetTable.newAddedRows.map(idx => idx + newRecords.length)
        ];
      }

      state.isLoading = false;
    },
    setDrmTableDataSuccess: (state, action) => {
      const targetTable = action.payload.tableType === "drmBidTableData" 
        ? state.drmBidTableData 
        : state.drmDailyTableData;
      
      targetTable.onPage = action.payload.onPage;
      targetTable.pageSize = action.payload.pageSize;
      targetTable.drmMaintenanceDetails = action.payload.drmMaintenanceDetails;
      targetTable.totalPages = action.payload.totalPages;
      targetTable.totalRecords = action.payload.totalRecords;
      targetTable.isLoading = false;
      
      state.isLoading = false;
    },
    setDrmUpdatedTableData: (state, action) => {
      const { tableType, drmUpdatedMaintenanceDetails } = action.payload;
      const targetTable =
        tableType === "drmBidTableData"
          ? state.drmBidTableData
          : state.drmDailyTableData;

      const [sagaId] = Object.keys(drmUpdatedMaintenanceDetails);
      const newColumns = drmUpdatedMaintenanceDetails[sagaId];

      const existingColumns =
        targetTable.drmUpdatedMaintenanceDetails[sagaId] || [];

      const updatedColumns = [...existingColumns];

      newColumns.forEach((newCol) => {
        const [columnId] = Object.keys(newCol);

        const columnExists = existingColumns.some((col) =>
          col.hasOwnProperty(columnId)
        );

        if (!columnExists) {
          updatedColumns.push(newCol);
        }
      });

      targetTable.drmUpdatedMaintenanceDetails = {
        ...targetTable.drmUpdatedMaintenanceDetails,
        [sagaId]: updatedColumns,
      };
    },
    setDrmTableDataFailure: (state, action) => {
      if (action.payload.tableType === "drmBidTableData") {
        state.drmBidTableData = {
          ...bidTableData,
          errorMessage: action.payload,
        };
      } else {
        state.drmDailyTableData = {
          ...dailyTableData,
          errorMessage: action.payload,
        };
      }
    },
    setOriginalRowData: (state, action) => {
      const { tableType, uniqueId, rowData } = action.payload;
      const targetTable = tableType === "drmBidTableData" ? state.drmBidTableData : state.drmDailyTableData;
      
      if (!targetTable.originalData[uniqueId]) {
        targetTable.originalData[uniqueId] = rowData;
      }
    },
    setRowModified: (state, action) => {
      const { tableType, uniqueId, isModified } = action.payload;
      const targetTable = tableType === "drmBidTableData" ? state.drmBidTableData : state.drmDailyTableData;
      
      if (isModified) {
        targetTable.modifiedRows[uniqueId] = true;
      } else {
        delete targetTable.modifiedRows[uniqueId];
      }
    },
    clearModifications: (state, action) => {
      const { tableType } = action.payload;
      const targetTable = tableType === "drmBidTableData" ? state.drmBidTableData : state.drmDailyTableData;
      
      targetTable.originalData = {};
      targetTable.modifiedRows = {};
      targetTable.drmUpdatedMaintenanceDetails = {};
      
      if (tableType === "drmDailyTableData") {
        targetTable.newAddedRows = [];
      }
    },
    setSelectedRows: (state, action) => {
      const { tableType, selectedRows } = action.payload;
      const targetTable = tableType === "drmBidTableData" ? state.drmBidTableData : state.drmDailyTableData;
      
      targetTable.selectedRows = selectedRows;
    },
    resetBidTableData: (state) => {
      state.drmBidTableData = { ...bidTableData };
    },
    resetDailyTableData: (state) => {
      state.drmDailyTableData = { ...dailyTableData };
    },
  },
});

export const {
  getDrmTableData,
  setDrmTableData,
  setDrmTableDataSuccess,
  setDrmTableDataFailure,
  setDrmUpdatedTableData,
  setOriginalRowData,
  setRowModified,
  clearModifications,
  setSelectedRows,
  resetBidTableData,
  resetDailyTableData,
} = drmTableDataSlice.actions;

export default drmTableDataSlice.reducer;
